import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import usePromptLibrary from "../../hooks/usePromptLibrary";
import { Input } from "@capacit-com/lara-law-shared-ui";
import MultiSelectField from "../filter/MultiSelectField";
import { Card } from "@capacit-com/lara-law-shared-ui";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import {
  Copy,
  Edit,
  EllipsisIcon,
  Eye,
  LineChart,
  MousePointerClick,
  Share,
  StarIcon,
  ThumbsDown,
  ThumbsUp,
  Trash,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import Prompt, { PromptRating, PromptStatus } from "@/models/prompt/prompt";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { TagType } from "@/models/tag/tag";
import PromptCardSkeleton from "./PromptCardSkeleton";
import usePromptPagination from "../../hooks/usePromptPagination";
import { debounce } from "lodash";
import DeleteDialog from "@/components/dialogs/delete-dialog";
import { Pill } from "@capacit-com/lara-law-shared-ui";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { showSuccessToast } from "@/components/custom-toasts/toasts";

type PromptLibraryListViewProps = {
  setInputValue: Dispatch<SetStateAction<string>>;
  handleNewPromptClicked: () => void;
  handleEditPromptClicked: (prompt: Prompt) => void;
  handlePreviewPrompt: (prompt: Prompt) => void;
  closePopover: () => void;
};

export type Option = {
  label: string;
  value: string;
};

type OptionWithGroupSelected = Option & {
  selectedLabel: string;
  group: string;
};

const PromptLibraryListView: FC<PromptLibraryListViewProps> = ({
  setInputValue,
  handleNewPromptClicked,
  handleEditPromptClicked,
  handlePreviewPrompt,
  closePopover,
}) => {
  const {
    promptsLoading,
    userPrompts,
    hasNextPage,
    loadNextPage,
    handleSearchChange,
    handleFiltering,
    handleSorting,
    setUserPrompts,
  } = usePromptPagination();

  const {
    handleRatePrompt,
    handleFavoritePrompt,
    handlePromptStatusToPending,
    handleUsePrompt,
    myUser,
    tags,
    handleDeletePrompt,
    crudPending,
  } = usePromptLibrary();

  const [sortOptions] = useState<OptionWithGroupSelected[]>([
    {
      selectedLabel: "Sorterer efter: Nyeste",
      label: "Nyeste",
      value: "createdAt:desc",
      group: "Oprettelse",
    },
    {
      selectedLabel: "Sorterer efter: Ældste",
      label: "Ældste",
      value: "createdAt:asc",
      group: "Oprettelse",
    },
    {
      selectedLabel: "Sorterer efter: Højeste rating",
      label: "Højeste rating",
      value: "promptRating:desc",
      group: "Rating",
    },
    {
      selectedLabel: "Sorterer efter: Laveste rating",
      label: "Laveste rating",
      value: "promptRating:asc",
      group: "Rating",
    },
  ]);

  const [filterOptions, setFilterOptions] = useState<OptionWithGroupSelected[]>(
    [
      {
        selectedLabel: "Filtrere efter: Rating >90%",
        label: ">90%",
        value: "promptRating_gte:90",
        group: "Rating",
      },
      {
        selectedLabel: "Filtrere efter: Rating >80%",
        label: ">80%",
        value: "promptRating_gte:80",
        group: "Rating",
      },
      {
        selectedLabel: "Filtrere efter: Rating >70%",
        label: ">70%",
        value: "promptRating_gte:70",
        group: "Rating",
      },
      {
        selectedLabel: "Filtrere efter: Rating >60%",
        label: ">60%",
        value: "promptRating_gte:60",
        group: "Rating",
      },
      {
        selectedLabel: "Filtrere efter: Rating >50%",
        label: ">50%",
        value: "promptRating_gte:50",
        group: "Rating",
      },
    ]
  );

  const [sortByOption, setSortByOption] = useState<OptionWithGroupSelected[]>([
    sortOptions[0],
  ]);
  const [filterByOptions, setFilterByOptions] = useState<
    OptionWithGroupSelected[] | undefined
  >(undefined);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState<Prompt | undefined>(
    undefined
  );

  const handleDeleteClicked = (prompt: Prompt) => {
    setIsDeleteOpen(true);
    setPromptToDelete(prompt);
  };

  const handleRatePromptAndUpdateState = (
    promptId: string,
    rating: PromptRating
  ) => {
    handleRatePrompt(promptId, rating);

    const isUserFilteringByLikes = filterByOptions?.some(
      (option) => option.label === "Dine likes"
    );
    const isUserFilteringByDislikes = filterByOptions?.some(
      (option) => option.label === "Dine dislikes"
    );

    // If filtering by likes/dislikes and removing the rating, remove the prompt from the list
    if (
      (isUserFilteringByLikes &&
        (rating === PromptRating.NeutralVote ||
          rating === PromptRating.DownVote)) ||
      (isUserFilteringByDislikes &&
        (rating === PromptRating.NeutralVote || rating === PromptRating.UpVote))
    ) {
      const updatedPrompts: Prompt[] = userPrompts.filter(
        (prompt) => prompt.id !== promptId
      );
      setUserPrompts(updatedPrompts);
      return;
    }

    const updatedPrompts: Prompt[] = userPrompts.map((prompt) => {
      if (prompt.id === promptId) {
        const newLikes =
          rating === PromptRating.UpVote
            ? [...prompt.likes, myUser?.id].filter(
                (likeId): likeId is string => !!likeId
              )
            : prompt.likes.filter((likeId) => likeId !== myUser?.id);
        const newDislikes =
          rating === PromptRating.DownVote
            ? [...prompt.dislikes, myUser?.id].filter(
                (dislikeId): dislikeId is string => !!dislikeId
              )
            : prompt.dislikes.filter((dislikeId) => dislikeId !== myUser?.id);

        const totalVotes = newLikes.length + newDislikes.length;
        const ratingPercentage =
          totalVotes === 0
            ? 0
            : Math.round((newLikes.length / totalVotes) * 100);

        return {
          ...prompt,
          promptRating: ratingPercentage,
          likes: newLikes,
          dislikes: newDislikes,
        };
      }
      return prompt;
    });
    setUserPrompts(updatedPrompts);
  };

  const handleFavoritePromptAndUpdateState = async (promptId: string) => {
    handleFavoritePrompt(promptId);
    const isUserFilteringByFavorites = filterByOptions?.some(
      (option) => option.label === "Favoritter"
    );

    if (isUserFilteringByFavorites) {
      const updatedPrompts: Prompt[] = userPrompts.filter(
        (prompt) => prompt.id !== promptId
      );
      setUserPrompts(updatedPrompts);
      return;
    }

    const updatedPrompts: Prompt[] = userPrompts.map((prompt) => {
      if (prompt.id === promptId) {
        return {
          ...prompt,
          favorites:
            myUser?.id && prompt.favorites.includes(myUser.id)
              ? prompt.favorites.filter(
                  (favoriteId) => favoriteId !== myUser?.id
                )
              : [...prompt.favorites, myUser?.id].filter(
                  (favoriteId): favoriteId is string => !!favoriteId
                ),
        };
      }
      return prompt;
    });
    setUserPrompts(updatedPrompts);
  };

  const handleUsePromptAndUpdateState = (prompt: Prompt) => {
    handleUsePrompt(prompt);
    setInputValue(prompt.promptString);
    const updatedPrompts: Prompt[] = userPrompts.map((p) => {
      if (p.id === prompt.id) {
        return {
          ...p,
          uses: p.uses + 1,
        };
      }
      return p;
    });
    setUserPrompts(updatedPrompts);
    closePopover();
  };

  const handlePromptStatusToPendingAndUpdateState = async (
    promptId: string
  ) => {
    try {
      await handlePromptStatusToPending(promptId);
      const updatedPrompts: Prompt[] = userPrompts.map((prompt) => {
        if (prompt.id === promptId) {
          return {
            ...prompt,
            status: PromptStatus.Pending,
          };
        }
        return prompt;
      });
      setUserPrompts(updatedPrompts);
      showSuccessToast("Promptens status er blevet opdateret");
    } catch (error) {
      logger.error("Failed to request prompt publication", {
        operation: "requestPromptPublication",
        error,
        promptId,
      });
      showErrorToast("Der skete en fejl ved opdatering af promptens status");
    }
  };

  const handleDeletePromptAndUpdateState = async (prompt: Prompt) => {
    try {
      await handleDeletePrompt(prompt.id);
      const updatedPrompts: Prompt[] = userPrompts.filter(
        (p) => p.id !== prompt.id
      );
      setUserPrompts(updatedPrompts);
      showSuccessToast("Prompten er blevet slettet");
    } catch (error) {
      logger.error("Failed to delete prompt", {
        operation: "deletePrompt",
        error,
        promptId: prompt.id,
      });
      showErrorToast("Der skete en fejl ved sletning af prompten");
    }
  };

  function handleCopyToClipboard(prompt: string) {
    navigator.clipboard.writeText(prompt);

    showSuccessToast("Prompten er blevet kopieret til udklipsholderen");
  }

  // Filter options that arent static will be populated here
  useEffect(() => {
    if (!myUser) return;

    // Filter out specific options
    const filteredOptions = filterOptions.filter(
      (element) =>
        !(
          element.group === "Tags" ||
          element.group === "Departments" ||
          element.group === "Gemte prompts"
        ) &&
        !(
          element.label === "Dine likes" || element.label === "Dine dislikes"
        ) &&
        !(element.label === "Favoritter" || element.label === "Mine prompts")
    );

    // Separate tags into categories
    const departmentTags = tags.filter(
      (tag) => tag.tagType === TagType.Department
    );
    const regularTags = tags.filter((tag) => tag.tagType === TagType.Tag);

    // Add new options
    const newOptions = [
      {
        selectedLabel: "Filtrer efter: Mine prompts",
        label: "Mine prompts",
        value: `userId:${myUser.id}`,
        group: "Gemte prompts",
      },
      {
        selectedLabel: "Filtrer efter: Favoritter",
        label: "Favoritter",
        value: `favorites:${myUser.id}`,
        group: "Gemte prompts",
      },
      ...filteredOptions,
      ...regularTags.map((tag) => ({
        selectedLabel: "Filtrer efter tag: " + tag.name,
        label: tag.name,
        value: `tags.name:${tag.name}`,
        group: "Tags",
      })),
      ...departmentTags.map((tag) => ({
        selectedLabel: "Filtrer efter afdeling: " + tag.name,
        label: tag.name,
        value: `tags.name:${tag.name}`,
        group: "Afdelinger",
      })),
      {
        selectedLabel: "Filtrer efter: Dine likes",
        label: "Dine likes",
        value: `likes:${myUser.id}`,
        group: "Rating",
      },
      {
        selectedLabel: "Filtrer efter: Dine dislikes",
        label: "Dine dislikes",
        value: `dislikes:${myUser.id}`,
        group: "Rating",
      },
    ];

    setFilterOptions(newOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, myUser]);

  const scrollPositionRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleScroll = useCallback(
    debounce(() => {
      const container = containerRef.current;
      if (!container) return;

      if (
        container.scrollTop + container.clientHeight + 420 >=
        container.scrollHeight
      ) {
        // Store the current scroll position before loading
        scrollPositionRef.current = container.scrollTop;
        loadNextPage();
      }
    }, 100),
    [loadNextPage]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || promptsLoading) return;

    // Restore scroll position after loading completes
    if (scrollPositionRef.current > 0) {
      container.scrollTop = scrollPositionRef.current;
    }
  }, [promptsLoading]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", debouncedHandleScroll);
      return () => {
        container.removeEventListener("scroll", debouncedHandleScroll);
      };
    }
  }, [debouncedHandleScroll]);

  const [localSearch, setLocalSearch] = useState("");

  return (
    <div className="flex flex-col min-h-[57vh] max-h-[67vh]">
      <div className="flex flex-row items-center gap-2 py-3 px-2 mt-1 justify-between">
        <div className="flex gap-2">
          <Input
            value={localSearch}
            onChange={(e) => {
              setLocalSearch(e.target.value);
              handleSearchChange(e.target.value);
            }}
            placeholder="Søg..."
            className="w-[15vw]"
          />
          <div className="flex flex-row gap-2">
            <MultiSelectField
              options={filterOptions}
              value={filterByOptions}
              placeholder="Filtrer efter"
              optionKey="label"
              groupKey="group"
              selectedOptionKey="selectedLabel"
              onChange={(option) => {
                if (filterByOptions?.[0].value === option.value) {
                  setFilterByOptions(undefined);
                  handleFiltering(undefined);
                  return;
                }
                setFilterByOptions([option]);
                handleFiltering(option);
              }}
            />
            <MultiSelectField
              options={sortOptions}
              value={sortByOption}
              placeholder="Sorter efter"
              optionKey="label"
              groupKey="group"
              selectedOptionKey="selectedLabel"
              onChange={(option) => {
                if (sortByOption?.[0].value === option.value) {
                  return; // Do nothing if the current selected option is clicked
                }
                setSortByOption([option]);
                handleSorting(option);
              }}
            />
          </div>
        </div>
        <Button onClick={handleNewPromptClicked}>Opret ny</Button>
      </div>

      <div
        className="flex-1 overflow-y-auto px-2 pb-3 space-y-2 min-h-0 py-1"
        ref={containerRef}
      >
        {promptsLoading && userPrompts.length === 0 ? (
          Array.from({ length: 5 }).map((_, index) => (
            <PromptCardSkeleton key={index} />
          ))
        ) : userPrompts.length > 0 ? (
          <>
            {userPrompts.map((prompt) => (
              <Card key={prompt.id} className="py-3 px-5">
                <div className="flex flex-row justify-between mb-2 flex-wrap">
                  <div className="flex gap-2 text-center items-center">
                    <p className="font-medium">{prompt.title}</p>
                    {prompt.status === PromptStatus.Pending && (
                      <p className="font-medium text-xs text-warning">
                        - Afventer godkendelse
                      </p>
                    )}
                    {prompt.status === PromptStatus.Personal && (
                      <p className="font-medium text-xs text-muted-foreground">
                        - Personlig prompt
                      </p>
                    )}
                    {prompt.status === PromptStatus.Rejected && (
                      <p className="font-medium text-xs text-destructive">
                        - Afvist prompt
                      </p>
                    )}
                  </div>

                  <div className="flex gap-1 flex-wrap items-center">
                    {prompt.tags.map((tag, idx) => (
                      <Pill pillLabel={tag.name} index={idx} />
                    ))}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="rounded-full w-7 h-7 bg-transparent hover:bg-secondary"
                          size="icon"
                        >
                          <EllipsisIcon />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handlePreviewPrompt(prompt)}
                        >
                          <Eye className="mr-2 size-4" />
                          <span>Forhåndvisning</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleCopyToClipboard(prompt.promptString)
                          }
                        >
                          <Copy className="mr-2 size-4" />
                          <span>Kopier</span>
                        </DropdownMenuItem>
                        {prompt.status === PromptStatus.Accepted ? (
                          <Tooltip delayDuration={10}>
                            <TooltipTrigger asChild>
                              <div>
                                <DropdownMenuItem
                                  disabled
                                  className="cursor-not-allowed"
                                >
                                  <Edit className="mr-2 size-4" />
                                  <span>Rediger</span>
                                </DropdownMenuItem>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                Du kan ikke redigere en offentliggjort prompt
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => handleEditPromptClicked(prompt)}
                          >
                            <Edit className="mr-2 size-4" />
                            <span>Rediger</span>
                          </DropdownMenuItem>
                        )}
                        {(prompt.status === PromptStatus.Personal ||
                          prompt.status === PromptStatus.Rejected) && (
                          <Tooltip delayDuration={10}>
                            <TooltipTrigger asChild>
                              <DropdownMenuItem
                                onClick={async () => {
                                  await handlePromptStatusToPendingAndUpdateState(
                                    prompt.id
                                  );
                                }}
                              >
                                <Share className="mr-2 size-4" />
                                <p>Admod om offentliggørelse</p>
                              </DropdownMenuItem>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                Anmod en prompt moderator til at offentliggøre
                                prompten for alle i organisationen.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {prompt.status === PromptStatus.Accepted ? (
                          <Tooltip delayDuration={10}>
                            <TooltipTrigger asChild>
                              <div>
                                <DropdownMenuItem
                                  disabled
                                  className="cursor-not-allowed"
                                >
                                  <Trash className="mr-2 size-4" />
                                  <p>Slet</p>
                                </DropdownMenuItem>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Du kan ikke slette en offentliggjort prompt</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => handleDeleteClicked(prompt)}
                          >
                            <Trash className="mr-2 size-4" />
                            <p>Slet</p>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div className="font-light overflow-hidden text-ellipsis">
                  {prompt.description}
                </div>
                <div className="flex flex-row justify-between mt-5">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className="rounded-full gap-1 hover:bg-secondary"
                    >
                      <div>
                        <ThumbsUp
                          size={12}
                          className={cn(
                            `hover:text-success cursor-pointer`,
                            prompt.likes.some(
                              (likeId) => likeId === myUser?.id
                            ) && `text-success`
                          )}
                          onClick={() => {
                            if (
                              prompt.likes.some(
                                (likeId) => likeId === myUser?.id
                              )
                            ) {
                              handleRatePromptAndUpdateState(
                                prompt.id,
                                PromptRating.NeutralVote
                              );
                            } else {
                              handleRatePromptAndUpdateState(
                                prompt.id,
                                PromptRating.UpVote
                              );
                            }
                          }}
                        />
                      </div>
                      {prompt.likes.length}
                      <div className="w-[1px] h-3 bg-secondary-foreground rounded-[999999px] mx-1" />
                      <div>
                        <ThumbsDown
                          size={12}
                          className={cn(
                            `hover:text-destructive cursor-pointer`,
                            prompt.dislikes.some(
                              (likeId) => likeId === myUser?.id
                            ) && `text-destructive`
                          )}
                          onClick={() => {
                            if (
                              prompt.dislikes.some(
                                (likeId) => likeId === myUser?.id
                              )
                            ) {
                              handleRatePromptAndUpdateState(
                                prompt.id,
                                PromptRating.NeutralVote
                              );
                            } else {
                              handleRatePromptAndUpdateState(
                                prompt.id,
                                PromptRating.DownVote
                              );
                            }
                          }}
                        />
                      </div>
                      {prompt.dislikes.length}
                    </Badge>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="inline-flex">
                          <Badge
                            variant="secondary"
                            className="rounded-full gap-1 hover:bg-secondary"
                          >
                            <LineChart size={18} />
                            <p>{prompt.promptRating} %</p>
                          </Badge>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Rating</p>
                      </TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="inline-flex">
                          <Badge
                            variant="secondary"
                            className="rounded-full gap-1 hover:bg-secondary"
                          >
                            <MousePointerClick size={18} />
                            <p>{prompt.uses}</p>
                          </Badge>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Antal brug</p>
                      </TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="inline-flex cursor-pointer">
                          <Badge
                            variant="secondary"
                            className="rounded-full gap-1 py-1 hover:bg-secondary"
                            onClick={() =>
                              handleFavoritePromptAndUpdateState(prompt.id)
                            }
                          >
                            <StarIcon
                              className={cn(
                                "hover:text-warning",
                                prompt.favorites.some(
                                  (favoriteId) => favoriteId === myUser?.id
                                ) && "text-warning"
                              )}
                            />
                          </Badge>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Tilføj til Favoritter</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  <Button
                    size="sm"
                    onClick={() => handleUsePromptAndUpdateState(prompt)}
                  >
                    Anvend
                  </Button>
                </div>
              </Card>
            ))}
            {hasNextPage &&
              Array.from({ length: 2 }).map((_, index) => (
                <PromptCardSkeleton key={index} />
              ))}
          </>
        ) : (
          <div className="flex justify-center items-center h-32">
            <p className="text-muted-foreground">Ingen prompts fundet</p>
          </div>
        )}
      </div>
      {promptToDelete && (
        <DeleteDialog
          customTrigger={true}
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
          onDelete={async () => {
            await handleDeletePromptAndUpdateState(promptToDelete);
            setPromptToDelete(undefined);
          }}
          entityName={promptToDelete.title}
          isDeleteLoading={crudPending}
          isDisabled={crudPending}
        />
      )}
    </div>
  );
};

export default PromptLibraryListView;
